import { FC, useEffect, useState} from "react";
import { useInView } from "react-cool-inview";
import { useMe } from "../../hooks/use-me";
import "intersection-observer";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { EllipsisCard, PageTitle } from "visible-ui";

import { SurveySection } from "../../model/survey";
import SurveyResponseQuestion from "./survey-response-question/SurveyResponseQuestion";
import { activeKeyAtom } from "../../state/atoms/atoms";
import { flatQuestionsSurveySelector } from "../../state/selectors/survey-selector";

interface SurveyResponseSectionProps {
  section: SurveySection;
  refs?: Record<string, React.RefObject<HTMLDivElement>>;
  hideTitle?: boolean;
}

const SurveyResponseSection: FC<SurveyResponseSectionProps> = ({
  section,
  hideTitle,
  refs = {},
}) => {
  const { observe: lastQuestionObserve } = useInView<HTMLDivElement>({
    threshold: 0.5, // 50% of the element needs to be visible in order to trigger
    onEnter: ({ entry }) => {
      // when element enters the screen set the eventKey
      setActiveKey(section.section_number.toString());
    },
  });

  const { observe: pageTitleObserve } = useInView<HTMLDivElement>({
    threshold: 1, // 100% of the element needs to be visible in order to trigger
    onEnter: ({ entry }) => {
      // when element enters the screen set the eventKey
      setActiveKey(section.section_number.toString());
    },
  });
  const { me, loading } = useMe();
  const [partial, setPartial] = useState(false);
  useEffect(() => {
    if(!loading){
      let p = (me?.sub_permissions)?.length !== 0;
      setPartial(p);
    }
  }, [me, loading]);
  const { total_questions, questions } = section;
  const setActiveKey = useSetRecoilState(activeKeyAtom);
  const flatQuestions = useRecoilValue(flatQuestionsSurveySelector);
  const questionNumbers = questions.map((question) => question.question_number);

  if(partial){
    return (
    <div data-testid="survey-response-section" className="mb-6">
      {!hideTitle && (
        <PageTitle
          ref={pageTitleObserve}
          divider
          dividerClassName="pt-0"
          preTitle={{
            text: `section ${String.fromCharCode(section.section_number + 65)}`,
          }}
          title={{ text: section.name }}
        />
      )}
      {questions.map((q, idx) => {
        const key = `survey-question-${section.id}-${q.question_number}`;
        return (
        <SurveyResponseQuestion
          key={key}
          question={q}
          cardRef={refs[q.id]}
          flatQuestions={flatQuestions}
          ref={idx === questions.length - 1 ? lastQuestionObserve : null}
        />);
        })
      }
      </div>
    );
  }else{
    return (
      <div data-testid="survey-response-section" className="mb-6">
        {!hideTitle && (
          <PageTitle
            ref={pageTitleObserve}
            divider
            dividerClassName="pt-0"
            preTitle={{
              text: `section ${String.fromCharCode(section.section_number + 65)}`,
            }}
            title={{ text: section.name }}
          />
        )}
        {[...Array(total_questions).keys()].map((number, idx) => {
          const currentQuestion = questions.find(
            ({ question_number }) =>  question_number === idx
          );
          const key = `survey-question-${section.id}-${number}`;
          if (currentQuestion) {
            return (
              <SurveyResponseQuestion
                key={key}
                question={currentQuestion}
                cardRef={refs[currentQuestion.id]}
                flatQuestions={flatQuestions}
                ref={number === questionNumbers[questionNumbers.length - 1] ? lastQuestionObserve : null}
              />
            );
          } else {
            return (
              <EllipsisCard
                data-testid="ellipsis-card"
                className="mb-4"
                key={key}
              />
            );
          }
        })}
      </div>
    );
  }
};

export default SurveyResponseSection;
